import type {
  EventQuery,
  EventResponse,
  PaginatedEventsResponse,
  SimulateEventsResponse,
} from '@monorepo/shared/schemas/events'
import {
  PaginatedEventsResponseSchema,
  SimulateEventsResponseSchema,
} from '@monorepo/shared/schemas/events'
import { get, post } from '@/lib/api-client'
import type { Signal } from '../types/events.types'

// ─── Adapter ─────────────────────────────────────────────────────────────────

export function eventResponseToSignal(e: EventResponse): Signal {
  return {
    id: e.id,
    signal_id: e.id,
    signal_name: e.event_name,
    signal_type: 'billable_signal',
    agent_id: e.agent_id,
    customer_id: e.customer_id ?? '',
    value: e.value ?? undefined,
    meta: e.properties ?? {},
    properties: e.properties ?? {},
    data: e.metadata ?? {},
    timestamp: e.timestamp,
    processed: e.processed ?? false,
    library: 'sdk',
    person: {
      id: e.customer_id ?? '',
    },
    created_at: e.created_at,
    updated_at: e.updated_at,
  }
}

// ─── Time range helper ────────────────────────────────────────────────────────

export function timeRangeToFromDate(
  timeRange: string | undefined,
  customFrom?: string
): string | undefined {
  const now = new Date()
  const ago = (ms: number) => new Date(now.getTime() - ms).toISOString()

  switch (timeRange) {
    case 'last_1h':
      return ago(1 * 60 * 60 * 1000)
    case 'last_24h':
      return ago(24 * 60 * 60 * 1000)
    case 'today':
      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      ).toISOString()
    case 'yesterday':
      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1
      ).toISOString()
    case 'last_7d':
      return ago(7 * 24 * 60 * 60 * 1000)
    case 'last_14d':
      return ago(14 * 24 * 60 * 60 * 1000)
    case 'last_30d':
      return ago(30 * 24 * 60 * 60 * 1000)
    case 'last_90d':
      return ago(90 * 24 * 60 * 60 * 1000)
    case 'last_180d':
      return ago(180 * 24 * 60 * 60 * 1000)
    case 'this_month':
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
    case 'year_to_date':
      return new Date(now.getFullYear(), 0, 1).toISOString()
    case 'all_time':
      return undefined
    case 'custom_from':
    case 'custom_range':
      return customFrom
    default:
      return ago(24 * 60 * 60 * 1000)
  }
}

// ─── Service ──────────────────────────────────────────────────────────────────

class EventsService {
  async getEvents(query: EventQuery = {}): Promise<PaginatedEventsResponse> {
    const params = new URLSearchParams()
    if (query.page) {
      params.append('page', query.page.toString())
    }
    if (query.limit) {
      params.append('limit', query.limit.toString())
    }
    if (query.event_name) {
      params.append('event_name', query.event_name)
    }
    if (query.agent_id) {
      params.append('agent_id', query.agent_id)
    }
    if (query.customer_id) {
      params.append('customer_id', query.customer_id)
    }
    if (query.from_date) {
      params.append('from_date', query.from_date)
    }
    if (query.to_date) {
      params.append('to_date', query.to_date)
    }
    if (query.sort) {
      params.append('sort', query.sort)
    }
    if (query.order) {
      params.append('order', query.order)
    }
    const url = `/events${params.toString() ? `?${params.toString()}` : ''}`
    return get(url, PaginatedEventsResponseSchema)
  }

  async simulateEvents(count: number): Promise<SimulateEventsResponse> {
    return post('/events/simulate', { count }, SimulateEventsResponseSchema)
  }
}

export const eventsService = new EventsService()
